"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHostVideoTrackRestartListener = void 0;
const react_1 = require("react");
const rxjs_1 = require("./rxjs");
const detect_browser_1 = require("detect-browser");
const use_video_enabled_1 = require("./use_video_enabled");
const use_is_backgrounded_1 = require("./use_is_backgrounded");
const logger_1 = require("../util/logger");
/**
 * This hook handles the following scenarios:
 *
 * If CV has been backgrounded while the video track is enabled
 * we restart the video track
 *
 * If the video track is subscribed to the room but not sending data,
 * the other participants will only see a black or frozen video feed.
 * To identify and resolve this issue, we inspect the webRTC stats during the call
 * A healthy local video track will never have a 'null' or '0' frame rate (for devices/browsers that support the getStats() frame rate property),
 * This is true even if the video controller is muted/disabled.
 * So, if we detect a frame rate from WebRTC stats below a set threshold,
 * we restart the track
 *
 * @param hostRoomSession
 *
 */
const useHostVideoTrackRestartListener = (hostRoomSession) => {
    const [localVideoController] = (0, rxjs_1.useObservable)(hostRoomSession.media.localVideoController);
    const [isLocalVideoFrozen] = (0, rxjs_1.useObservable)(hostRoomSession.media.isLocalVideoFrozen);
    const [videoEnabled] = (0, use_video_enabled_1.useVideoEnabled)(hostRoomSession.media.localVideoController);
    const [isBackgrounded] = (0, use_is_backgrounded_1.useIsBackgrounded)(hostRoomSession.media.localVideoController);
    const [isRestarting, setIsRestarting] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        const browser = (0, detect_browser_1.detect)();
        // Safari versions below iOS 16 will always report a 'null' frameRate
        // We don't want to spam-restart video tracks from false positives, so we check first for an supported iOS version match
        // https://www.webrtc-developers.com/webrtc-statistics-using-getstats/#browsers-differentiation
        // https://w3c-test.org/webrtc-stats/supported-stats.html
        const isIosVersionGreaterOrEqualTo16 = (browser === null || browser === void 0 ? void 0 : browser.os) === 'iOS' && parseFloat(browser === null || browser === void 0 ? void 0 : browser.version) >= 16.0;
        const handleWebRtcVideoTrackStatusCheck = () => __awaiter(void 0, void 0, void 0, function* () {
            if (isBackgrounded) {
                setIsRestarting(true);
                yield (localVideoController === null || localVideoController === void 0 ? void 0 : localVideoController.handleBackgroundRestart());
                setIsRestarting(false);
                return;
            }
            if (isLocalVideoFrozen && videoEnabled) {
                setIsRestarting(true);
                yield (localVideoController === null || localVideoController === void 0 ? void 0 : localVideoController.handlePoorQualityRestart());
                setIsRestarting(false);
                return;
            }
        });
        if (!(localVideoController === null || localVideoController === void 0 ? void 0 : localVideoController.restart)) {
            return;
        }
        if (isRestarting) {
            logger_1.logger.debug('Attempting to restart video track while one is already in progress');
            return;
        }
        if (!isIosVersionGreaterOrEqualTo16) {
            logger_1.logger.debug('Aborting video track restart for unsupported iOS version');
            return;
        }
        handleWebRtcVideoTrackStatusCheck().catch(e => logger_1.logger.error('Error restarting video track ', e));
        // Watching isRestarting causes the hook to run more than we want it to
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localVideoController, videoEnabled, isLocalVideoFrozen, isBackgrounded]);
};
exports.useHostVideoTrackRestartListener = useHostVideoTrackRestartListener;
//# sourceMappingURL=use_host_video_track_restart_listener.js.map